import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { AlertsBell } from "@/components/AlertsBell";
import { Wifi, WifiOff, DatabaseZap } from "lucide-react";

export function Header() {
  const { t, lang, setLang } = useI18n();
  const [status, setStatus] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [cachedAt, setCachedAt] = useState(null);

  useEffect(() => {
    const onHit = (e) => setCachedAt(e.detail?.at || Date.now());
    const onOnline = () => setCachedAt(null);
    window.addEventListener("bvd:cache-hit", onHit);
    window.addEventListener("online", onOnline);
    return () => { window.removeEventListener("bvd:cache-hit", onHit); window.removeEventListener("online", onOnline); };
  }, []);

  const loadAlerts = useCallback(() => {
    api.get(`/alerts?lang=${lang}`).then((r) => setAlerts(r.data)).catch(() => {});
  }, [lang]);

  useEffect(() => {
    const load = () => {
      api.get("/system/status").then((r) => setStatus(r.data)).catch(() => {});
      loadAlerts();
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [loadAlerts]);

  return (
    <header className="sticky top-0 z-10 h-14 border-b border-slate-200 bg-white/90 backdrop-blur-md flex items-center justify-between px-6">
      <div className="text-xs font-medium tracking-wide text-slate-500">{t("app_sub")}</div>
      <div className="flex items-center gap-3">
        {cachedAt && (
          <span data-testid="cache-indicator" className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border text-slate-600 border-slate-300 bg-slate-50" title={t("cache_hint")}>
            <DatabaseZap className="w-3.5 h-3.5" /> {t("cache_label")} {new Date(cachedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
        <AlertsBell alerts={alerts} reload={loadAlerts} />
        {status && (
          <span data-testid="connectivity-indicator" className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${status.queued_pushes > 0 ? "text-amber-700 border-amber-200 bg-amber-50" : "text-emerald-700 border-emerald-200 bg-emerald-50"}`}>
            {status.queued_pushes > 0 ? <WifiOff className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
            {status.queued_pushes > 0 ? `${t("status_offline")} (${status.queued_pushes})` : t("status_online")}
          </span>
        )}
        <div className="flex items-center gap-0.5 border border-slate-200 rounded-md p-0.5">
          {["fr", "en"].map((l) => (
            <button
              key={l}
              data-testid={`language-toggle-${l}`}
              onClick={() => setLang(l)}
              className={`px-2.5 py-1 rounded text-xs font-semibold uppercase transition-colors ${
                lang === l ? "bg-[#0B2A5B] text-white" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
